/**
 * Composable for accessing feature flags
 *
 * Provides reactive access to feature flags from tenant configuration.
 */

import { computed, type ComputedRef } from 'vue'
import { getConfig } from '@/config'

interface UseFeatureFlagsReturn {
  /** Whether minor selection step is enabled */
  minorSelectionEnabled: ComputedRef<boolean>
  /** Whether two-to-four year workflow is enabled */
  twoToFourYearEnabled: ComputedRef<boolean>
  /** Whether saved plans functionality is enabled */
  savedPlansEnabled: ComputedRef<boolean>
}

/**
 * Access feature flags for the current tenant
 *
 * @example
 * ```ts
 * const { minorSelectionEnabled, savedPlansEnabled } = useFeatureFlags()
 *
 * // In template
 * // <div v-if="minorSelectionEnabled">...</div>
 * ```
 */
export function useFeatureFlags(): UseFeatureFlagsReturn {
  const config = getConfig()

  const minorSelectionEnabled = computed(() => config.features.minorSelection)
  const twoToFourYearEnabled = computed(() => config.features.twoToFourYear)
  const savedPlansEnabled = computed(() => config.features.savedPlans)

  return {
    minorSelectionEnabled,
    twoToFourYearEnabled,
    savedPlansEnabled,
  }
}
